// Additional services of a company on an engagement-creating form —
// Engagement Registration (New / Modify) and Management → New Engagement
// (docs/modules/workflow-engreg.md "Additional Services"). A ticked company
// gets one engagement for the Service Provided it answers (the form's, or its
// own) and one more for every service line added under it: same FYE, people
// and client profile; its own service, Description of Service and Estimated
// Fees. Shared by the forms and the server (CommonJS for the same reason as
// qcQuestions.js). No object spread here: the production build turns it into
// an ESM helper import, and webpack then no longer sees this file's exports.

// The fee-split key of a service line (company rows key by entity id).
const LINE_FEE_PREFIX = 'line:';
const lineFeeKey = (k) => `${LINE_FEE_PREFIX}${k}`;
const isLineFeeKey = (key) => String(key || '').startsWith(LINE_FEE_PREFIX);

// The row keys of the live duplicate-check: a company's own slot, a line's.
const ownRowKey = (entityId) => `own:${entityId}`;
const lineRowKey = (k) => `line:${k}`;
const isLineRowKey = (key) => String(key || '').startsWith('line:');

/** The service text an engagement carries (Other → the typed text). */
const effectiveService = (service, serviceOther) => (
  String(service || '').trim() === 'Other'
    ? String(serviceOther || '').trim() || 'Other'
    : String(service || '').trim()
);

// Descriptions compare whole, trimmed, whitespace-collapsed and
// case-insensitive (the engagement slot rule, services/engagementSlot.js).
const normDescription = (s) => String(s || '').trim().replace(/\s+/g, ' ').toLowerCase();

const firstLine = (s) => String(s || '').split(/\r?\n/)[0].trim();

// A line, a company's own answers or a form: both spellings of the two
// fields the fee rows name an engagement by.
const serviceOf = (o) => effectiveService(
  o.serviceProvided !== undefined ? o.serviceProvided : o.service, o.serviceOther
);
const descriptionOf = (o) => firstLine(
  o.descriptionOfService !== undefined ? o.descriptionOfService : o.description
);

/** "Agreed Upon Procedures (IFRS reporting)" — a line as the lists print it. */
const serviceLineText = (o = {}) => {
  const svc = serviceOf(o);
  const desc = descriptionOf(o);
  return desc ? `${svc} (${desc})` : svc;
};

/**
 * "Agreed Upon Procedures · AUP on the annual statement" — the grey line
 * under a fee row's company, so every row of Estimated Fees per Engagement
 * says which engagement it is, the company's own as well as its lines.
 */
const serviceSubtitle = (o = {}) => {
  const svc = serviceOf(o);
  const desc = descriptionOf(o);
  return [svc, desc].filter(Boolean).join(' · ');
};

const DUPLICATE_ENGAGEMENT_CODE = 'DUPLICATE_ENGAGEMENT';
const DESCRIPTION_REQUIRED_CODE = 'DESCRIPTION_REQUIRED';
const DUPLICATE_DESCRIPTION_CODE = 'DUPLICATE_DESCRIPTION';

/**
 * The slots one form fills twice. `items` = every engagement the form will
 * create, in form order — each company's own service first, then its lines:
 * [{ key, entityId, entityName, service, serviceOther, description }].
 * `multiServices` = the Service Provided values flagged "Multiple per FYE".
 *
 * Returns one row per problem ({ key, entityId, entityName, service, code,
 * message, short }) — `message` names the company (the server's refusal, the
 * submit banner), `short` is the line under the row itself. The server
 * refuses with the first:
 *   - a service without the flag picked twice for one company → DUPLICATE_ENGAGEMENT
 *   - a flagged service picked twice: every repeat after the first needs a
 *     Description of Service of its own → DESCRIPTION_REQUIRED
 *   - two repeats with one description → DUPLICATE_DESCRIPTION
 * (Against the master and the review queue each item is still asked on its
 * own — services/engagementCoverage.findCoverageClashes.)
 */
const repeatedSlotClashes = (items, multiServices = []) => {
  const multi = new Set((multiServices || []).map((v) => String(v).trim().toLowerCase()));
  const bySlot = new Map();
  for (const it of items || []) {
    const svc = effectiveService(it.service, it.serviceOther);
    if (!it.entityId || !svc) continue;
    const slot = `${it.entityId}|${svc.toLowerCase()}`;
    if (!bySlot.has(slot)) bySlot.set(slot, []);
    bySlot.get(slot).push(Object.assign({}, it, { svc }));
  }
  const out = [];
  for (const group of bySlot.values()) {
    if (group.length < 2) continue;
    const { entityId, entityName, svc } = group[0];
    if (!multi.has(svc.toLowerCase())) {
      group.slice(1).forEach((it) => out.push({
        key: it.key, entityId, entityName, service: svc, code: DUPLICATE_ENGAGEMENT_CODE,
        message: `${entityName} has ${svc} twice on this form — one engagement per company per FYE per service`,
        short: `${svc} is already on this form for this company — one engagement per service.`,
      }));
      continue;
    }
    const seen = new Map();
    group.forEach((it, i) => {
      const d = normDescription(it.description);
      if (i > 0 && !d) {
        out.push({
          key: it.key, entityId, entityName, service: svc, code: DESCRIPTION_REQUIRED_CODE,
          message: `${entityName} has ${svc} more than once on this form — give each one after the first a Description of Service of its own`,
          short: 'Add a Description of Service to tell this one apart.',
        });
        return;
      }
      if (d && seen.has(d)) {
        out.push({
          key: it.key, entityId, entityName, service: svc, code: DUPLICATE_DESCRIPTION_CODE,
          message: `${entityName}: two ${svc} engagements on this form share one Description of Service — describe them differently`,
          short: 'This Description of Service is already used on this form — describe this one differently.',
        });
        return;
      }
      if (d) seen.set(d, it.key);
    });
  }
  return out;
};

/**
 * "Company: Service (description); Company: Service" — the request's service
 * lines as one line (History, mail). Null when the request has none.
 */
const serviceLinesText = (request) => {
  const lines = (request && request.serviceLines) || [];
  if (lines.length === 0) return null;
  return lines.map((l) => `${l.entityName}: ${serviceLineText(l)}`).join('; ');
};

module.exports = {
  LINE_FEE_PREFIX, lineFeeKey, isLineFeeKey, ownRowKey, lineRowKey, isLineRowKey,
  effectiveService, normDescription, firstLine, serviceLineText, serviceSubtitle, serviceLinesText, repeatedSlotClashes,
  DUPLICATE_ENGAGEMENT_CODE, DESCRIPTION_REQUIRED_CODE, DUPLICATE_DESCRIPTION_CODE,
};
